import { useEffect, useRef } from 'react'

/*
 * Aceternity UI dotted glow background
 * (ui.aceternity.com/components/dotted-glow-background), adapted:
 * - colours come from palette tokens (`--foreground`, `--primary`...);
 * - under reduced motion it paints one still frame instead of looping;
 * - the unused radial fade option is gone.
 */
export function DottedGlowBackground(props: {
	className?: string
	/** Distance between dot centres, px. */
	gap?: number
	/** Dot radius, px. */
	radius?: number
	/** Theme token for the dots, e.g. `--foreground`. */
	colorVar: string
	/** Theme token for the glow of the brightest dots. */
	glowColorVar: string
	/** Opacity of the whole layer. */
	opacity?: number
	/** Per-dot pulse speed range, rad/s. */
	speedMin?: number
	speedMax?: number
}) {
	const canvasRef = useRef<HTMLCanvasElement | null>(null)
	const containerRef = useRef<HTMLDivElement | null>(null)
	const gap = props.gap ?? 12
	const radius = props.radius ?? 2
	const opacity = props.opacity ?? 0.6
	const speedMin = props.speedMin ?? 0.4
	const speedMax = props.speedMax ?? 1.3
	const colorVar = props.colorVar
	const glowColorVar = props.glowColorVar

	useEffect(() => {
		const canvas = canvasRef.current
		const container = containerRef.current
		const ctx = canvas?.getContext('2d')
		if (!(canvas && container && ctx)) {
			return
		}

		const styles = getComputedStyle(document.documentElement)
		const color = styles.getPropertyValue(colorVar).trim()
		const glow = styles.getPropertyValue(glowColorVar).trim()

		const dpr = Math.min(Math.max(1, window.devicePixelRatio || 1), 2)
		let dots: { x: number; y: number; phase: number; speed: number }[] = []
		const layout = () => {
			const { width, height } = container.getBoundingClientRect()
			canvas.width = Math.max(1, Math.floor(width * dpr))
			canvas.height = Math.max(1, Math.floor(height * dpr))
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
			dots = []
			const cols = Math.ceil(width / gap) + 2
			const rows = Math.ceil(height / gap) + 2
			for (let i = -1; i < cols; i++) {
				for (let j = -1; j < rows; j++) {
					dots.push({
						// Every other row shifts half a step: a staggered grid.
						x: i * gap + (j % 2 === 0 ? 0 : gap * 0.5),
						y: j * gap,
						phase: Math.random() * Math.PI * 2,
						speed:
							speedMin +
							Math.random() * Math.max(speedMax - speedMin, 0)
					})
				}
			}
		}
		layout()

		const draw = (now: number) => {
			ctx.clearRect(0, 0, canvas.width, canvas.height)
			ctx.fillStyle = color
			const time = now / 1000
			for (const dot of dots) {
				// Triangle wave 0..1..0: each dot brightens and dims linearly.
				const mod = (time * dot.speed + dot.phase) % 2
				const alpha = 0.25 + 0.55 * (mod < 1 ? mod : 2 - mod)
				if (alpha > 0.6) {
					ctx.shadowColor = glow
					ctx.shadowBlur = 6 * ((alpha - 0.6) / 0.4)
				} else {
					ctx.shadowColor = 'transparent'
					ctx.shadowBlur = 0
				}
				ctx.globalAlpha = alpha * opacity
				ctx.beginPath()
				ctx.arc(dot.x, dot.y, radius, 0, Math.PI * 2)
				ctx.fill()
			}
		}

		const still = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches
		let raf = 0
		const loop = (now: number) => {
			draw(now)
			raf = requestAnimationFrame(loop)
		}
		if (still) {
			draw(0)
		} else {
			raf = requestAnimationFrame(loop)
		}

		const resizeObserver = new ResizeObserver(() => {
			layout()
			if (still) {
				draw(0)
			}
		})
		resizeObserver.observe(container)

		return () => {
			cancelAnimationFrame(raf)
			resizeObserver.disconnect()
		}
	}, [gap, radius, opacity, speedMin, speedMax, colorVar, glowColorVar])

	return (
		<div
			aria-hidden='true'
			className={props.className}
			ref={containerRef}
			style={{ position: 'absolute', inset: 0 }}
		>
			<canvas
				ref={canvasRef}
				style={{ display: 'block', width: '100%', height: '100%' }}
			/>
		</div>
	)
}
